import { useState, useMemo, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Badge } from "../../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import {
    Database,
    Users,
    Monitor,
    Building2,
    UserCheck,
    Shield,
    Search,
    ChevronRight,
    ChevronDown,
    ChevronLeft,
    ChevronsLeft,
    ChevronsRight,
    User,
    Server,
    Globe,
    Layers,
    AlertTriangle,
    CheckCircle,
    Minus,
    Crown,
    Key,
    Settings,
    FileText,
    X,
    ExternalLink,
    Loader2,
    UserPlus,
    UserMinus
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { useToast } from "../../../hooks/use-toast";
import { getUsers, getDevices, organizationUnits, getUserGroupMemberships, getOUUsers, getOUDevices, updateUserGroupMembership } from "./Api/activedirectoryapi";
import useUserStore from "../../../services/userStore";
import { myLocalStorage } from "../../StorageHelper";
import { ADDevice, ADDomain, ADOrganizationUnit, ADUser, UserGroupMembership } from "./interface";
import { useNavigate } from "react-router-dom";




export function extractDomainFromDN(
    dn: string,
    uniqueDomains: Set<string> // pass a Set for O(1) inserts & uniqueness
): string | null {
    if (!dn) return null;

    const matches = dn.match(/DC=([^,]+)/gi);
    if (!matches) return null;

    const domainParts = matches.map(dc => dc.split("=")[1].trim());
    const domain = domainParts.join(".");

    // Insert domain into the unique Set
    uniqueDomains.add(domain);

    return domain;
}

export function buildDomainTree(domains: string[]): Record<string, any> {
    const root: Record<string, any> = {};

    for (const domain of domains) {
        const parts = domain.split("."); // ["team","region","corp","local"]
        const reversedParts = parts.reverse(); // ["local","corp","region","team"]

        let pointer = root;

        for (const part of reversedParts) {
            if (!pointer[part]) {
                pointer[part] = {};
            }
            pointer = pointer[part];
        }
    }

    return root;
}

/**
 * Convenience function:
 * Takes the entire response array and returns:
 *   { domainsList: string[], tree: object }
 */
export function processUsersForDomains(
    users: Array<{ DistinguishedName?: string }>
) {
    const uniqueDomains = new Set<string>();

    for (const user of users) {
        if (user?.DistinguishedName) {
            extractDomainFromDN(user.DistinguishedName, uniqueDomains);
        }
    }

    const domainsList = Array.from(uniqueDomains);
    const tree = buildDomainTree(domainsList);

    return { domainsList, tree };
}


const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function ActiveDirectoryManagement() {
    const latestTenant = useMemo(() => {
        const tenant = myLocalStorage.getItem("latestTenant");
        return tenant?.tenantName || "";
    }, []);

    const [adUsers, setAdUsers] = useState<ADUser[]>([]);
    const [adDevices, setAdDevices] = useState<ADDevice[]>([]);
    const [adOUs, setAdOUs] = useState<ADOrganizationUnit[]>([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [isLoadingDevices, setIsLoadingDevices] = useState(false);
    const [isLoadingOUs, setIsLoadingOUs] = useState(false);

    const [selectedDomain, setSelectedDomain] = useState<ADDomain | null>(null);
    const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set(['contoso']));
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [activeTab, setActiveTab] = useState("services");
    const [selectedUser, setSelectedUser] = useState<ADUser | null>(null);
    const [isDomainSidebarCollapsed, setIsDomainSidebarCollapsed] = useState(false);
    const [groupModalOpen, setGroupModalOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<string>("");
    const [selectedGroupsList, setSelectedGroupsList] = useState<string[]>([]);
    const [groupUserSearch, setGroupUserSearch] = useState("");
    const [usersPage, setUsersPage] = useState(1);
    const [usersPageSize, setUsersPageSize] = useState(25);
    const [servicesPage, setServicesPage] = useState(1);
    const [servicesPageSize, setServicesPageSize] = useState(25);
    const [devicesPage, setDevicesPage] = useState(1);
    const [devicesPageSize, setDevicesPageSize] = useState(25);
    const [groupsPage, setGroupsPage] = useState(1);
    const [groupsPageSize, setGroupsPageSize] = useState(25);
    const [ousPage, setOusPage] = useState(1);
    const [ousPageSize, setOusPageSize] = useState(25);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedGroupMembership, setSelectedGroupMembership] = useState<UserGroupMembership | null>(null);
    const [addUserToGroupModalOpen, setAddUserToGroupModalOpen] = useState(false);
    const [removeUserModalOpen, setRemoveUserModalOpen] = useState(false);
    const [selectedMemberToRemove, setSelectedMemberToRemove] = useState<string>("");
    const [userSearchForGroup, setUserSearchForGroup] = useState("");
    const [confirmAddUserModalOpen, setConfirmAddUserModalOpen] = useState(false);
    const [selectedUserToAdd, setSelectedUserToAdd] = useState<ADUser | null>(null);
    const [userGroupsModalOpen, setUserGroupsModalOpen] = useState(false);
    const [selectedUserForGroups, setSelectedUserForGroups] = useState<ADUser | null>(null);
    const [userGroupMemberships, setUserGroupMemberships] = useState<UserGroupMembership[]>([]);
    const [userGroupsSearch, setUserGroupsSearch] = useState("");
    const [showGroupMembersModalOpen, setShowGroupMembersModalOpen] = useState(false);
    const [groupMembersSearch, setGroupMembersSearch] = useState("");
    const [ouUsersModalOpen, setOuUsersModalOpen] = useState(false);
    const [selectedOU, setSelectedOU] = useState<ADOrganizationUnit | null>(null);
    const [ouUsers, setOuUsers] = useState<ADUser[]>([]);
    const [ouUsersSearch, setOuUsersSearch] = useState("");

    const [ouDevicesModalOpen, setOuDevicesModalOpen] = useState(false);
    const [selectedOUForDevices, setSelectedOUForDevices] = useState<ADOrganizationUnit | null>(null);
    const [ouDevices, setOuDevices] = useState<ADDevice[]>([]);
    const [ouDevicesSearch, setOuDevicesSearch] = useState("");
    const [isAddingUser, setIsAddingUser] = useState(false);
    const [isRemovingUser, setIsRemovingUser] = useState(false);

    const navigate = useNavigate();

    const { toast } = useToast();

    useEffect(() => {
        const fetchADUsers = async () => {
            if (!latestTenant) return;
            setIsLoadingUsers(true);
            try {
                const data = await getUsers(latestTenant);
                setAdUsers(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error("Error fetching AD users:", error);
                toast({
                    title: "Error",
                    description: "Failed to load Active Directory users",
                    variant: "destructive"
                });
                setAdUsers([]);
            } finally {
                setIsLoadingUsers(false);
            }
        };
        fetchADUsers();
    }, [latestTenant]);

    useEffect(() => {
        const fetchADDevices = async () => {
            if (!latestTenant) return;
            setIsLoadingDevices(true);
            try {
                const data = await getDevices(latestTenant);
                setAdDevices(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error("Error fetching AD devices:", error);
                toast({
                    title: "Error",
                    description: "Failed to load Active Directory devices",
                    variant: "destructive"
                });
                setAdDevices([]);
            } finally {
                setIsLoadingDevices(false);
            }
        };
        fetchADDevices();
    }, [latestTenant]);

    useEffect(() => {
        const fetchOUs = async () => {
            if (!latestTenant) return;
            setIsLoadingOUs(true);
            try {
                const data = await organizationUnits(latestTenant);
                setAdOUs(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error("Error fetching organization units:", error);
                toast({
                    title: "Error",
                    description: "Failed to load organization units",
                    variant: "destructive"
                });
                setAdOUs([]);
            } finally {
                setIsLoadingOUs(false);
            }
        };
        fetchOUs();
    }, [latestTenant]);

    useEffect(() => {
        const fetchGroupMemberships = async () => {
            if (!latestTenant) return;
            if (adUsers.length === 0) return;
            try {
                const data = await getUserGroupMemberships(latestTenant, adUsers[0].id || -1);
                setUserGroupMemberships(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error("Error fetching group memberships:", error);
                toast({
                    title: "Error",
                    description: "Failed to load group memberships",
                    variant: "destructive"
                });
                setUserGroupMemberships([]);
            }
        };
        fetchGroupMemberships();
    }, [latestTenant, adUsers]);

    useEffect(() => {
        setIsSearching(true);
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setUsersPage(1);
            setServicesPage(1);
            setDevicesPage(1);
            setGroupsPage(1);
            setOusPage(1);
            setIsSearching(false);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const toggleDomainExpansion = (domainId: string) => {
        const newExpanded = new Set(expandedDomains);
        if (newExpanded.has(domainId)) {
            newExpanded.delete(domainId);
        } else {
            newExpanded.add(domainId);
        }
        setExpandedDomains(newExpanded);
    };

    const generatedDomains = useMemo(() => {
        const uniqueDomains = new Set<string>();
        const allItems = [...adUsers, ...adDevices];

        for (const item of allItems) {
            if ('DistinguishedName' in item && item.DistinguishedName) {
                extractDomainFromDN(item.DistinguishedName, uniqueDomains);
            }
        }

        const domainsList = Array.from(uniqueDomains);

        return domainsList.map((fqdn) => {
            // Extract DC parts for exact matching (e.g., DC=WSAD2,DC=local)
            const dcParts = fqdn.split('.').map(part => `DC=${part}`).join(',');

            // Calculate counts specific to this domain by matching DC parts exactly
            const domainUsers = adUsers.filter(u => {
                const userDomain = extractDomainFromDN(u.DistinguishedName, new Set());
                return userDomain === fqdn;
            });
            if (domainUsers.length === 0) {
                return;
            }

            const domainDevices = adDevices.filter(d => {
                const deviceDomain = extractDomainFromDN(d.DistinguishedName, new Set());
                return deviceDomain === fqdn;
            });

            const domainOUs = adOUs.filter(ou => {
                const ouDN = ou.distinguishedName || '';
                const ouDomain = extractDomainFromDN(ouDN, new Set());
                return ouDomain === fqdn;
            });

            return {
                id: fqdn,
                name: fqdn,
                fqdn,
                type: 'root' as const,
                parent: undefined,
                userCount: domainUsers.length,
                deviceCount: domainDevices.length,
                serviceAccountCount: domainUsers.filter(u => u.isServiceAccount).length,
                groupCount: 0,
                ouCount: domainOUs.length
            };
        });
    }, [adUsers, adDevices, adOUs]); const isSingleDomainSetup = useMemo(() => {
        const rootDomains = generatedDomains.filter(d => d?.type === 'root');
        if (rootDomains.length !== 1) return false;
        const children = generatedDomains.filter(d => d?.parent === rootDomains[0]?.id);
        return children.length === 0;
    }, [generatedDomains]);

    useEffect(() => {
        if (!selectedDomain && generatedDomains.length > 0) {
            setSelectedDomain(generatedDomains[0] || null);
        }
    }, [generatedDomains, selectedDomain]);

    const renderDomainTree = (domain: ADDomain, level: number = 0) => {
        const children = generatedDomains.filter(d => d?.parent === domain?.id);
        const isExpanded = expandedDomains.has(domain.id);
        const isSelected = selectedDomain?.id === domain.id;

        if (isSingleDomainSetup && level === 0) {
            return (
                <div key={domain.id}>
                    <div
                        className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800"
                        data-testid={`domain-${domain.id}`}
                    >
                        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-800/50 rounded-lg flex items-center justify-center">
                            <Database className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold truncate">{domain.name}</div>
                            <div className="text-xs text-muted-foreground">
                                {domain.userCount.toLocaleString()} users • {domain.serviceAccountCount.toLocaleString()} services
                            </div>
                        </div>
                        <Crown className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                    </div>
                </div>
            );
        }

        return (
            <div key={domain.id}>
                <div
                    className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800' : 'hover-elevate'
                        }`}
                    style={{ paddingLeft: `${level * 20 + 8}px` }}
                    onClick={() => setSelectedDomain(domain)}
                    data-testid={`domain-${domain.id}`}
                >
                    {children.length > 0 && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-4 w-4 p-0"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleDomainExpansion(domain.id);
                            }}
                        >
                            {isExpanded ? (
                                <ChevronDown className="w-3 h-3" />
                            ) : (
                                <ChevronRight className="w-3 h-3" />
                            )}
                        </Button>
                    )}
                    <Database className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{domain.name}</div>
                        <div className="text-xs text-muted-foreground">
                            {domain.userCount.toLocaleString()} users • {domain.serviceAccountCount.toLocaleString()} services
                        </div>
                    </div>
                    {domain.type === 'root' && (
                        <Crown className="w-3 h-3 text-yellow-600 dark:text-yellow-400" />
                    )}
                </div>
                {isExpanded && children.map(child => child && renderDomainTree(child, level + 1))}
            </div>
        );
    };

    const filteredUsers = useMemo(() => {
        const query = debouncedSearch.toLowerCase();
        return adUsers.filter(user => {
            if (user.isServiceAccount) return false;
            if (selectedDomain) {
                const userDomain = extractDomainFromDN(user.DistinguishedName, new Set());
                if (userDomain !== selectedDomain.fqdn) return false;
            }
            if (query === '') return true;
            const name = (user.Name || '').toLowerCase();
            const sam = (user.SamAccountName || '').toLowerCase();
            const upn = (user.UserPrincipalName || '').toLowerCase();
            const email = (user.EmailAddress || '').toLowerCase();
            const dn = (user.DistinguishedName || '').toLowerCase();
            const id = String(user.id || '').toLowerCase();
            const mobile = (user.MobilePhone || '').toLowerCase();
            return (
                name.includes(query) ||
                sam.includes(query) ||
                upn.includes(query) ||
                email.includes(query) ||
                dn.includes(query) ||
                id.includes(query) ||
                mobile.includes(query)
            );
        });
    }, [debouncedSearch, adUsers, selectedDomain]);

    const filteredServiceAccounts = useMemo(() => {
        const query = debouncedSearch.toLowerCase();
        return adUsers.filter(account => {
            if (!account.isServiceAccount) return false;
            if (selectedDomain) {
                const accountDomain = extractDomainFromDN(account.DistinguishedName, new Set());
                if (accountDomain !== selectedDomain.fqdn) return false;
            }
            if (query === '') return true;
            const name = (account.Name || '').toLowerCase();
            const sam = (account.SamAccountName || '').toLowerCase();
            const upn = (account.UserPrincipalName || '').toLowerCase();
            const email = (account.EmailAddress || '').toLowerCase();
            const dn = (account.DistinguishedName || '').toLowerCase();
            const id = String(account.id || '').toLowerCase();
            const mobile = (account.MobilePhone || '').toLowerCase();
            return (
                name.includes(query) ||
                sam.includes(query) ||
                upn.includes(query) ||
                email.includes(query) ||
                dn.includes(query) ||
                id.includes(query) ||
                mobile.includes(query)
            );
        });
    }, [debouncedSearch, adUsers, selectedDomain]);

    const filteredDevices = useMemo(() => {
        const query = debouncedSearch.toLowerCase();
        return adDevices.filter(device => {
            if (selectedDomain) {
                const deviceDomain = extractDomainFromDN(device.DistinguishedName, new Set());
                if (deviceDomain !== selectedDomain.fqdn) return false;
            }
            if (query === '') return true;
            const name = (device.Name || '').toLowerCase();
            const dns = (device.DNSHostName || '').toLowerCase();
            const os = (device.OperatingSystem || '').toLowerCase();
            const ip = (device.IPv4Address || '').toLowerCase();
            const dn = (device.DistinguishedName || '').toLowerCase();
            const endpointId = (device.endpointId || '').toLowerCase();
            return (
                name.includes(query) ||
                dns.includes(query) ||
                os.includes(query) ||
                ip.includes(query) ||
                dn.includes(query) ||
                endpointId.includes(query)
            );
        });
    }, [debouncedSearch, adDevices, selectedDomain]);


    const filteredGroups = useMemo(() => {
        const query = debouncedSearch.toLowerCase();
        return userGroupMemberships.filter(membership => {
            if (selectedDomain) {
                const groupDomain = extractDomainFromDN(membership.adUserGroup.DistinguishedName, new Set());
                if (groupDomain !== selectedDomain.fqdn) return false;
            }
            if (query === '') return true;
            const name = (membership.adUserGroup.Name || '').toLowerCase();
            const sam = (membership.adUserGroup.SamAccountName || '').toLowerCase();
            const dn = (membership.adUserGroup.DistinguishedName || '').toLowerCase();
            const cn = (membership.adUserGroup.cn || '').toLowerCase();
            const id = String(membership.adUserGroup.id || '').toLowerCase();
            const membersJoined = membership.adUserGroup.Members.join(' ').toLowerCase();
            return (
                name.includes(query) ||
                sam.includes(query) ||
                dn.includes(query) ||
                cn.includes(query) ||
                id.includes(query) ||
                membersJoined.includes(query)
            );
        });
    }, [debouncedSearch, userGroupMemberships, selectedDomain]);


    const filteredOUs = useMemo(() => {
        const query = debouncedSearch.toLowerCase();
        return adOUs.filter(ou => {
            if (selectedDomain) {
                const ouDomain = extractDomainFromDN(ou.distinguishedName || '', new Set());
                if (ouDomain !== selectedDomain.fqdn) return false;
            }
            if (query === '') return true;
            const name = (ou.name || '').toLowerCase();
            const cn = (ou.cn || '').toLowerCase();
            const dn = (ou.distinguishedName || '').toLowerCase();
            const id = String(ou.id || '').toLowerCase();
            return (
                name.includes(query) ||
                cn.includes(query) ||
                dn.includes(query) ||
                id.includes(query)
            );
        });
    }, [debouncedSearch, adOUs, selectedDomain]);

    const paginatedUsers = useMemo(() => {
        const start = (usersPage - 1) * usersPageSize;
        return filteredUsers.slice(start, start + usersPageSize);
    }, [filteredUsers, usersPage, usersPageSize]);

    const paginatedServiceAccounts = useMemo(() => {
        const start = (servicesPage - 1) * servicesPageSize;
        return filteredServiceAccounts.slice(start, start + servicesPageSize);
    }, [filteredServiceAccounts, servicesPage, servicesPageSize]);

    const paginatedDevices = useMemo(() => {
        const start = (devicesPage - 1) * devicesPageSize;
        return filteredDevices.slice(start, start + devicesPageSize);
    }, [filteredDevices, devicesPage, devicesPageSize]);

    const paginatedGroups = useMemo(() => {
        const start = (groupsPage - 1) * groupsPageSize;
        return filteredGroups.slice(start, start + groupsPageSize);
    }, [filteredGroups, groupsPage, groupsPageSize]);

    const paginatedOUs = useMemo(() => {
        const start = (ousPage - 1) * ousPageSize;
        return filteredOUs.slice(start, start + ousPageSize);
    }, [filteredOUs, ousPage, ousPageSize]);

    const totalUsersPages = Math.ceil(filteredUsers.length / usersPageSize);
    const totalServicesPages = Math.ceil(filteredServiceAccounts.length / servicesPageSize);
    const totalDevicesPages = Math.ceil(filteredDevices.length / devicesPageSize);
    const totalGroupsPages = Math.ceil(filteredGroups.length / groupsPageSize);
    const totalOUsPages = Math.ceil(filteredOUs.length / ousPageSize);

    const extractCNFromDN = (dn: string): string => {
        const cnMatch = dn.match(/^CN=([^,]+)/);
        return cnMatch ? cnMatch[1] : dn;
    };

    const parseModifiedDate = (dateStr: string): string => {
        const match = dateStr.match(/\/Date\((\d+)\)\//);
        if (match) {
            const timestamp = parseInt(match[1], 10);
            return new Date(timestamp).toLocaleDateString();
        }
        return dateStr;
    };

    const getUserGroupsForUser = (user: ADUser): UserGroupMembership[] => {
        const userNameLower = (user.Name || "").toLowerCase();
        const samLower = (user.SamAccountName || "").toLowerCase();
        const dnLower = (user.DistinguishedName || "").toLowerCase();
        const cnLower = extractCNFromDN(user.DistinguishedName || "").toLowerCase();
        const emailLower = (user.EmailAddress || "").toLowerCase();
        const upnLower = (user.UserPrincipalName || "").toLowerCase();

        const identifiers = [dnLower, cnLower, samLower, userNameLower, emailLower, upnLower].filter(Boolean);

        return userGroupMemberships.filter(m => {
            const memberMatch = m.adUserGroup.Members.some(memberDN => {
                const memberLower = (memberDN || "").toLowerCase();
                return identifiers.some(id => memberLower.includes(id));
            });

            const adUser = m.adUser;
            const adUserDN = (adUser?.DistinguishedName || "").toLowerCase();
            const adUserSam = (adUser?.SamAccountName || "").toLowerCase();
            const adUserName = (adUser?.Name || "").toLowerCase();
            const adUserUpn = (adUser?.UserPrincipalName || "").toLowerCase();
            const adUserEmail = (adUser?.EmailAddress || "").toLowerCase();

            const recordMatch = identifiers.some(id =>
                adUserDN.includes(id) ||
                adUserSam.includes(id) ||
                adUserName.includes(id) ||
                (adUserUpn && adUserUpn.includes(id)) ||
                (adUserEmail && adUserEmail.includes(id))
            );

            return memberMatch || recordMatch;
        });
    };

    const handleAddUserToGroup = async (groupId: number, user: ADUser) => {
        if (!latestTenant) return;

        setIsAddingUser(true);
        try {
            await updateUserGroupMembership(latestTenant, {
                userId: user.id,
                groupId: groupId,
                groupStatus: "ENABLED",
                selectedTime: 3600
            });

            const userDN = user.DistinguishedName;
            setUserGroupMemberships(prev => {
                const updated = prev.map(membership => {
                    if (membership.adUserGroup.id === groupId) {
                        if (!membership.adUserGroup.Members.includes(userDN)) {
                            const newMembership = {
                                ...membership,
                                adUserGroup: {
                                    ...membership.adUserGroup,
                                    Members: [...membership.adUserGroup.Members, userDN]
                                }
                            };
                            if (selectedGroupMembership?.adUserGroup.id === groupId) {
                                setSelectedGroupMembership(newMembership);
                            }
                            return newMembership;
                        }
                    }
                    return membership;
                });
                return updated;
            });

            toast({
                title: "User Added",
                description: `${user.Name} has been added to the group successfully.`,
            });
            setAddUserToGroupModalOpen(false);
            setUserSearchForGroup("");
        } catch (error) {
            console.error("Error adding user to group:", error);
            toast({
                title: "Error",
                description: "Failed to add user to group. Please try again.",
                variant: "destructive"
            });
        } finally {
            setIsAddingUser(false);
        }
    };

    const handleRemoveUserFromGroup = async (groupId: number, memberDN: string) => {
        if (!latestTenant) return;

        // Find the user by their Distinguished Name
        const user = adUsers.find(u => u.DistinguishedName === memberDN);
        if (!user) {
            toast({
                title: "Error",
                description: "User not found.",
                variant: "destructive"
            });
            return;
        }

        setIsRemovingUser(true);
        try {
            await updateUserGroupMembership(latestTenant, {
                userId: user.id,
                groupId: groupId,
                groupStatus: "DISABLED",
                selectedTime: 3600
            });

            setUserGroupMemberships(prev => {
                const updated = prev.map(membership => {
                    if (membership.adUserGroup.id === groupId) {
                        const newMembership = {
                            ...membership,
                            adUserGroup: {
                                ...membership.adUserGroup,
                                Members: membership.adUserGroup.Members.filter(m => m !== memberDN)
                            }
                        };
                        if (selectedGroupMembership?.adUserGroup.id === groupId) {
                            setSelectedGroupMembership(newMembership);
                        }
                        return newMembership;
                    }
                    return membership;
                });
                return updated;
            });

            toast({
                title: "User Removed",
                description: `User has been removed from the group successfully.`,
            });
            setRemoveUserModalOpen(false);
            setSelectedMemberToRemove("");
        } catch (error) {
            console.error("Error removing user from group:", error);
            toast({
                title: "Error",
                description: "Failed to remove user from group. Please try again.",
                variant: "destructive"
            });
        } finally {
            setIsRemovingUser(false);
        }
    };


    const handleShowOUUsers = async (ou: ADOrganizationUnit) => {
        setSelectedOU(ou);
        setOuUsersSearch("");
        setOuUsersModalOpen(true);
        setOuUsers([]); // Clear previous data

        try {
            const users = await getOUUsers(ou.id);
            setOuUsers(Array.isArray(users) ? users : []);
        } catch (error) {
            console.error("Error fetching OU users:", error);
            toast({
                title: "Error",
                description: `Failed to load users for ${ou.name}`,
                variant: "destructive"
            });
            setOuUsers([]);
        }
    };


    const handleShowOUDevices = async (ou: ADOrganizationUnit) => {
        setSelectedOUForDevices(ou);
        setOuDevicesSearch("");
        setOuDevicesModalOpen(true);
        setOuDevices([]); // Clear previous data

        try {
            const devices = await getOUDevices(ou.id);
            setOuDevices(Array.isArray(devices) ? devices : []);
        } catch (error) {
            console.error("Error fetching OU devices:", error);
            toast({
                title: "Error",
                description: `Failed to load devices for ${ou.name}`,
                variant: "destructive"
            });
            setOuDevices([]);
        }
    };

    const getPageNumbers = (currentPage: number, totalPages: number) => {
        const pages: (number | string)[] = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            if (currentPage <= 3) {
                pages.push(1, 2, 3, 4, '...', totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
            } else {
                pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
            }
        }
        return pages;
    };

    const renderPaginationControls = (
        currentPage: number,
        totalPages: number,
        totalItems: number,
        pageSize: number,
        setPage: (page: number) => void,
        setPageSize: (size: number) => void,
        entityName: string
    ) => {
        const startItem = (currentPage - 1) * pageSize + 1;
        const endItem = Math.min(currentPage * pageSize, totalItems);
        const pageNumbers = getPageNumbers(currentPage, totalPages);

        return (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-border bg-muted/10">
                <div className="flex items-center gap-4">
                    <div className="text-sm text-muted-foreground">
                        Showing <span className="font-medium text-foreground">{totalItems > 0 ? startItem : 0}</span> to{' '}
                        <span className="font-medium text-foreground">{endItem}</span> of{' '}
                        <span className="font-medium text-foreground">{totalItems.toLocaleString()}</span> {entityName}
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Per page:</span>
                        <Select value={pageSize.toString()} onValueChange={(val) => { setPageSize(Number(val)); setPage(1); }}>
                            <SelectTrigger className="w-[70px] h-8" data-testid={`select-pagesize-${entityName}`}>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {PAGE_SIZE_OPTIONS.map(size => (
                                    <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="flex items-center gap-1">
                    <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setPage(1)}
                        disabled={currentPage === 1}
                        data-testid={`button-first-${entityName}`}
                    >
                        <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        data-testid={`button-prev-${entityName}`}
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </Button>

                    <div className="flex items-center gap-1 mx-2">
                        {pageNumbers.map((page, idx) => (
                            typeof page === 'number' ? (
                                <Button
                                    key={idx}
                                    variant={currentPage === page ? "default" : "outline"}
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    onClick={() => setPage(page)}
                                    data-testid={`button-page-${page}-${entityName}`}
                                >
                                    {page}
                                </Button>
                            ) : (
                                <span key={idx} className="px-2 text-muted-foreground">...</span>
                            )
                        ))}
                    </div>

                    <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setPage(currentPage + 1)}
                        disabled={currentPage === totalPages || totalPages === 0}
                        data-testid={`button-next-${entityName}`}
                    >
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setPage(totalPages)}
                        disabled={currentPage === totalPages || totalPages === 0}
                        data-testid={`button-last-${entityName}`}
                    >
                        <ChevronsRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        );
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'active':
            case 'online':
                return <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />;
            case 'disabled':
            case 'offline':
                return <Minus className="w-4 h-4 text-red-600 dark:text-red-400" />;
            case 'locked':
            case 'unknown':
                return <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />;
            default:
                return null;
        }
    };

    return (
        <div className="flex-1 flex flex-col h-screen bg-muted/30">
            <div className="flex items-center gap-4 px-6 py-4 border-b border-border bg-card shadow-sm">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-sm">
                    <Database className="w-5 h-5 text-white" />
                </div>
                <div>
                    <h1 className="text-xl font-semibold tracking-tight" data-testid="text-page-title">
                        Active Directory
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        Enterprise identity and access management
                    </p>
                </div>
            </div>

            <div className="flex flex-1 overflow-hidden">
                <div className={`border-r border-border bg-card shadow-sm transition-all duration-300 ${isDomainSidebarCollapsed ? 'w-12' : 'w-72'}`}>
                    <div className="flex h-full flex-col">
                        <div className="flex h-11 items-center justify-between px-3 border-b border-border">
                            {!isDomainSidebarCollapsed && (
                                <div className="flex items-center gap-2">
                                    <Building2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                    <span className="font-medium text-sm">Domain Forest</span>
                                </div>
                            )}
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setIsDomainSidebarCollapsed(!isDomainSidebarCollapsed)}
                                className="h-7 w-7 p-0"
                                data-testid="button-collapse-sidebar"
                            >
                                {isDomainSidebarCollapsed ? (
                                    <ChevronRight className="w-4 h-4" />
                                ) : (
                                    <ChevronLeft className="w-4 h-4" />
                                )}
                            </Button>
                        </div>

                        {!isDomainSidebarCollapsed && (
                            <div className="flex-1 overflow-auto p-3">
                                <div className="space-y-1">
                                    {generatedDomains.filter(d => d?.type === 'root').map(domain => domain && renderDomainTree(domain))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Main Enterprise Content Area */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    {/* Domain Info Header */}
                    <div className="h-14 border-b border-border bg-card px-6 flex items-center justify-between shadow-sm">
                        <div className="flex items-center gap-3">
                            {selectedDomain && (
                                <>
                                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/50 rounded-md flex items-center justify-center">
                                        <Database className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div>
                                        <h2 className="font-medium text-sm">{selectedDomain.fqdn}</h2>
                                        <p className="text-xs text-muted-foreground">
                                            {selectedDomain.userCount.toLocaleString()} users • {selectedDomain.serviceAccountCount.toLocaleString()} services
                                        </p>
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="relative w-72">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                                <Input
                                    placeholder="Search users, services, devices..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-9 pr-10 h-9 bg-background"
                                    data-testid="input-search-domain"
                                />
                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center justify-center">
                                    {isSearching ? (
                                        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                                    ) : searchQuery ? (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6 p-0 hover:bg-muted"
                                            onClick={() => setSearchQuery('')}
                                            data-testid="button-clear-search"
                                        >
                                            <X className="w-4 h-4" />
                                        </Button>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Enterprise Top Level Tabs */}
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
                        <div className="border-b border-border bg-card flex-shrink-0">
                            <TabsList className="w-full bg-transparent p-0 h-12 justify-start rounded-none">
                                <TabsTrigger
                                    value="services"
                                    className="gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm border-b-2 border-transparent data-[state=active]:border-primary rounded-none px-6 h-12"
                                >
                                    <Settings className="w-4 h-4" />
                                    Service Accounts ({filteredServiceAccounts.length.toLocaleString()})

                                </TabsTrigger>
                                <TabsTrigger
                                    value="users"
                                    className="gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm border-b-2 border-transparent data-[state=active]:border-primary rounded-none px-6 h-12"
                                >
                                    <Users className="w-4 h-4" />
                                    Users ({filteredUsers.length.toLocaleString()})

                                </TabsTrigger>
                                <TabsTrigger
                                    value="groups"
                                    className="gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm border-b-2 border-transparent data-[state=active]:border-primary rounded-none px-6 h-12"
                                    data-testid="tab-groups"
                                >
                                    <Shield className="w-4 h-4" />
                                    Groups ({filteredGroups.length.toLocaleString()})

                                </TabsTrigger>
                                <TabsTrigger
                                    value="ous"
                                    className="gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm border-b-2 border-transparent data-[state=active]:border-primary rounded-none px-6 h-12"
                                    data-testid="tab-ous"
                                >
                                    <Layers className="w-4 h-4" />
                                    Organization Units ({filteredOUs.length.toLocaleString()})

                                </TabsTrigger>
                                <TabsTrigger
                                    value="devices"
                                    className="gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm border-b-2 border-transparent data-[state=active]:border-primary rounded-none px-6 h-12"
                                >
                                    <Monitor className="w-4 h-4" />
                                    Devices ({filteredDevices.length.toLocaleString()})

                                </TabsTrigger>
                            </TabsList>
                        </div>

                        {/* Content Area */}
                        <div className="flex-1 overflow-hidden p-6">
                            {/* Users Tab */}
                            <TabsContent value="users" className="mt-0 h-full data-[state=active]:flex data-[state=active]:flex-col">
                                <Card className="flex-1 flex flex-col overflow-hidden">
                                    <div className="bg-muted/20 px-6 py-3 border-b border-border flex-shrink-0">
                                        <div className="grid gap-4 items-center" style={{ gridTemplateColumns: '1fr 1fr 1.5fr 80px' }}>
                                            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Name</div>
                                            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Email</div>
                                            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Distinguished Name</div>
                                            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">Groups</div>
                                        </div>
                                    </div>
                                    <CardContent className="p-0 flex-1 overflow-auto">
                                        {isLoadingUsers ? (
                                            <div className="flex items-center justify-center py-12">
                                                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                                                <span className="ml-2 text-muted-foreground">Loading users...</span>
                                            </div>
                                        ) : isSearching ? (
                                            <div className="flex items-center justify-center py-12">
                                                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                                                <span className="ml-2 text-muted-foreground">Searching...</span>
                                            </div>
                                        ) : paginatedUsers.length === 0 ? (
                                            <div className="text-center py-12">
                                                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                                <h3 className="text-lg font-medium text-muted-foreground">No Users Found</h3>
                                                <p className="text-sm text-muted-foreground">
                                                    {debouncedSearch ? `No users match "${debouncedSearch}"` : 'No users available'}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="divide-y divide-border">
                                                {paginatedUsers.map((user) => {
                                                    const userGroups = getUserGroupsForUser(user);
                                                    return (
                                                        <div
                                                            key={user.id}
                                                            className="px-6 py-4 hover-elevate cursor-pointer"
                                                            onClick={() => navigate(`/activeDirectory/user/${user.id}/${user.Name}/${user.SamAccountName}`)}
                                                            data-testid={`link-user-${user.id}`}
                                                        >
                                                            <div className="grid gap-4 items-center" style={{ gridTemplateColumns: '1fr 1fr 1.5fr 80px' }}>
                                                                <div className="flex items-center gap-3" >
                                                                    <User className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                                                    <div className="min-w-0">
                                                                        <div className="font-medium text-sm truncate text-blue-600 dark:text-blue-400 hover:underline">{user.Name}</div>
                                                                        <div className="text-xs text-muted-foreground truncate">{user.SamAccountName}</div>
                                                                    </div>
                                                                </div>
                                                                <div className="text-sm text-muted-foreground truncate">
                                                                    {user.EmailAddress || '-'}
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <div
                                                                        className="text-xs font-mono truncate text-muted-foreground"
                                                                        title={user.DistinguishedName}
                                                                    >
                                                                        {user.DistinguishedName}
                                                                    </div>
                                                                </div>
                                                                <div className="flex justify-end">
                                                                    <Button
                                                                        variant="outline"
                                                                        size="sm"
                                                                        className="h-7 px-3 text-xs font-medium text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-950/50 hover:border-blue-300 dark:hover:border-blue-700 transition-colors"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setSelectedUserForGroups(user);
                                                                            setUserGroupsModalOpen(true);
                                                                        }}
                                                                        data-testid={`button-view-groups-${user.id}`}
                                                                    >
                                                                        <Shield className="w-3.5 h-3.5 mr-1.5" />
                                                                        {userGroups.length}
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </CardContent>
                                    {!isSearching && filteredUsers.length > 0 && renderPaginationControls(
                                        usersPage,
                                        totalUsersPages,
                                        filteredUsers.length,
                                        usersPageSize,
                                        setUsersPage,
                                        setUsersPageSize,
                                        'users'
                                    )}
                                </Card>
                            </TabsContent>

                            {/* Service Accounts Tab */}
                            <TabsContent value="services" className="mt-0 h-full data-[state=active]:flex data-[state=active]:flex-col">
                                <Card className="flex-1 flex flex-col overflow-hidden">
                                    <div className="bg-muted/20 px-6 py-3 border-b border-border flex-shrink-0">
                                        <div className="grid gap-4 items-center" style={{ gridTemplateColumns: '1fr 1fr 1.5fr 80px' }}>
                                            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Service Account</div>
                                            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Email</div>
                                            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Distinguished Name</div>
                                            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">Groups</div>
                                        </div>
                                    </div>
                                    <CardContent className="p-0 flex-1 overflow-auto">
                                        {isLoadingUsers ? (
                                            <div className="flex items-center justify-center py-12">
                                                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                                                <span className="ml-2 text-muted-foreground">Loading service accounts...</span>
                                            </div>
                                        ) : isSearching ? (
                                            <div className="flex items-center justify-center py-12">
                                                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                                                <span className="ml-2 text-muted-foreground">Searching...</span>
                                            </div>
                                        ) : paginatedServiceAccounts.length === 0 ? (
                                            <div className="text-center py-12">
                                                <Settings className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                                <h3 className="text-lg font-medium text-muted-foreground">No Service Accounts Found</h3>
                                                <p className="text-sm text-muted-foreground">
                                                    {debouncedSearch ? `No service accounts match "${debouncedSearch}"` : 'No service accounts available'}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="divide-y divide-border">
                                                {paginatedServiceAccounts.map((account) => {
                                                    const accountGroups = getUserGroupsForUser(account);
                                                    return (
                                                        <div
                                                            key={account.id}
                                                            className="px-6 py-4 hover-elevate cursor-pointer"
                                                            onClick={() => navigate(`/activeDirectory/service/${account.id}/${account.Name}`)}
                                                            data-testid={`row-service-${account.id}`}
                                                        >
                                                            <div className="grid gap-4 items-center" style={{ gridTemplateColumns: '1fr 1fr 1.5fr 80px' }}>
                                                                <div className="flex items-center gap-3">
                                                                    <Settings className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                                                    <div className="min-w-0">
                                                                        <div className="font-medium text-sm truncate text-blue-600 dark:text-blue-400 hover:underline">{account.Name}</div>
                                                                        <div className="text-xs text-muted-foreground truncate">{account.SamAccountName}</div>
                                                                    </div>
                                                                </div>
                                                                <div className="text-sm text-muted-foreground truncate">
                                                                    {account.EmailAddress || '-'}
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <div
                                                                        className="text-xs font-mono truncate text-muted-foreground"
                                                                        title={account.DistinguishedName}
                                                                    >
                                                                        {account.DistinguishedName}
                                                                    </div>
                                                                </div>
                                                                <div className="flex justify-end">
                                                                    <Button
                                                                        variant="outline"
                                                                        size="sm"
                                                                        className="h-7 px-3 text-xs font-medium text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-950/50 hover:border-blue-300 dark:hover:border-blue-700 transition-colors"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setSelectedUserForGroups(account);
                                                                            setUserGroupsModalOpen(true);
                                                                        }}
                                                                        data-testid={`button-view-svc-groups-${account.id}`}
                                                                    >
                                                                        <Shield className="w-3.5 h-3.5 mr-1.5" />
                                                                        {accountGroups.length}
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </CardContent>
                                    {!isSearching && filteredServiceAccounts.length > 0 && renderPaginationControls(
                                        servicesPage,
                                        totalServicesPages,
                                        filteredServiceAccounts.length,
                                        servicesPageSize,
                                        setServicesPage,
                                        setServicesPageSize,
                                        'services'
                                    )}
                                </Card>
                            </TabsContent>

                            {/* Organization Units Tab */}
                            <TabsContent value="ous" className="mt-0 h-full data-[state=active]:flex data-[state=active]:flex-col">
                                <Card className="flex-1 flex flex-col overflow-hidden">
                                    <div className="bg-muted/20 px-6 py-3 border-b border-border flex-shrink-0">
                                        <div className="text-sm font-medium text-muted-foreground">ORGANIZATION UNIT</div>
                                    </div>
                                    <CardContent className="p-0 flex-1 overflow-auto">
                                        {isLoadingOUs ? (
                                            <div className="flex items-center justify-center py-12">
                                                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                                                <span className="ml-2 text-muted-foreground">Loading organization units...</span>
                                            </div>
                                        ) : isSearching ? (
                                            <div className="flex items-center justify-center py-12">
                                                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                                                <span className="ml-2 text-muted-foreground">Searching...</span>
                                            </div>
                                        ) : paginatedOUs.length === 0 ? (
                                            <div className="text-center py-12">
                                                <Layers className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                                <h3 className="text-lg font-medium text-muted-foreground">No Organization Units Found</h3>
                                                <p className="text-sm text-muted-foreground">
                                                    {debouncedSearch ? `No OUs match "${debouncedSearch}"` : 'No organization units available'}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="divide-y divide-border">
                                                {paginatedOUs.map((ou) => (
                                                    <div
                                                        key={ou.id}
                                                        className="px-6 py-3"
                                                        data-testid={`row-ou-${ou.id}`}
                                                    >
                                                        <div className="flex items-start gap-3">
                                                            <Layers className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                                                            <div className="min-w-0 flex-1">
                                                                <div className="flex items-center justify-between gap-2">
                                                                    <span className="font-medium">{ou.name}</span>
                                                                    {/* Action Buttons - Show Users and Show Devices in right corner */}
                                                                    <div className="flex items-center gap-2 flex-shrink-0">
                                                                        {/* Users Button - Opens modal with users in this OU */}
                                                                        <Button
                                                                            variant="outline"
                                                                            size="sm"
                                                                            className="h-7 px-3 text-xs font-medium text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-950/50 hover:border-blue-300 dark:hover:border-blue-700 transition-colors"
                                                                            onClick={() => handleShowOUUsers(ou)}
                                                                            data-testid={`button-show-ou-users-${ou.id}`}
                                                                        >
                                                                            <Users className="w-3.5 h-3.5 mr-1.5" />
                                                                            Users
                                                                        </Button>
                                                                        {/* Devices Button - Opens modal with devices/computers in this OU */}
                                                                        <Button
                                                                            variant="outline"
                                                                            size="sm"
                                                                            className="h-7 px-3 text-xs font-medium text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-950/50 hover:border-blue-300 dark:hover:border-blue-700 transition-colors"
                                                                            onClick={() => handleShowOUDevices(ou)}
                                                                            data-testid={`button-show-ou-devices-${ou.id}`}
                                                                        >
                                                                            <Monitor className="w-3.5 h-3.5 mr-1.5" />
                                                                            Devices
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                                <div
                                                                    className="text-xs font-mono text-muted-foreground/70 mt-1 truncate"
                                                                    title={ou.distinguishedName}
                                                                >
                                                                    {ou.distinguishedName}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                    {!isSearching && filteredOUs.length > 0 && renderPaginationControls(
                                        ousPage,
                                        totalOUsPages,
                                        filteredOUs.length,
                                        ousPageSize,
                                        setOusPage,
                                        setOusPageSize,
                                        'ous'
                                    )}
                                </Card>
                            </TabsContent>

                            {/* Devices Tab */}
                            <TabsContent value="devices" className="mt-0 h-full data-[state=active]:flex data-[state=active]:flex-col">
                                <Card className="flex-1 flex flex-col overflow-hidden">
                                    <div className="bg-muted/20 px-6 py-3 border-b border-border flex-shrink-0">
                                        <div className="text-sm font-medium text-muted-foreground">DEVICE</div>
                                    </div>
                                    <CardContent className="p-0 flex-1 overflow-auto">
                                        {isLoadingDevices ? (
                                            <div className="flex items-center justify-center py-12">
                                                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                                                <span className="ml-2 text-muted-foreground">Loading devices...</span>
                                            </div>
                                        ) : isSearching ? (
                                            <div className="flex items-center justify-center py-12">
                                                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                                                <span className="ml-2 text-muted-foreground">Searching...</span>
                                            </div>
                                        ) : paginatedDevices.length === 0 ? (
                                            <div className="text-center py-12">
                                                <Monitor className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                                <h3 className="text-lg font-medium text-muted-foreground">No Devices Found</h3>
                                                <p className="text-sm text-muted-foreground">
                                                    {debouncedSearch ? `No devices match "${debouncedSearch}"` : 'No devices available'}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="divide-y divide-border">
                                                {paginatedDevices.map((device, index) => (
                                                    <div
                                                        key={device.Name + index}
                                                        className="px-6 py-3"
                                                        data-testid={`row-device-${device.Name}`}
                                                    >
                                                        <div className="flex items-start gap-3">
                                                            <Monitor className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
                                                            <div className="min-w-0 flex-1">
                                                                <div className="font-medium">{device.Name}</div>
                                                                {device.OperatingSystem && (
                                                                    <div className="text-sm text-muted-foreground">{device.OperatingSystem}</div>
                                                                )}
                                                                <div
                                                                    className="text-xs font-mono text-muted-foreground/70 mt-1 truncate"
                                                                    title={device.DistinguishedName}
                                                                >
                                                                    {device.DistinguishedName}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                    {!isSearching && filteredDevices.length > 0 && renderPaginationControls(
                                        devicesPage,
                                        totalDevicesPages,
                                        filteredDevices.length,
                                        devicesPageSize,
                                        setDevicesPage,
                                        setDevicesPageSize,
                                        'devices'
                                    )}
                                </Card>
                            </TabsContent>

                            {/* Groups Tab */}
                            <TabsContent value="groups" className="mt-0 h-full data-[state=active]:flex data-[state=active]:flex-col">
                                <Card className="flex-1 flex flex-col overflow-hidden">
                                    <div className="bg-muted/20 px-6 py-3 border-b border-border flex-shrink-0">
                                        <div className="grid gap-4 items-center" style={{ gridTemplateColumns: '1fr 100px 180px' }}>
                                            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Group Name</div>
                                            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider text-center">Members</div>
                                            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">Actions</div>
                                        </div>
                                    </div>
                                    <CardContent className="p-0 flex-1 overflow-auto">
                                        {isSearching ? (
                                            <div className="flex items-center justify-center py-12">
                                                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                                                <span className="ml-2 text-muted-foreground">Searching...</span>
                                            </div>
                                        ) : paginatedGroups.length === 0 ? (
                                            <div className="text-center py-12">
                                                <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                                <h3 className="text-lg font-medium text-muted-foreground">No Groups Found</h3>
                                                <p className="text-sm text-muted-foreground">
                                                    {debouncedSearch ? `No groups match "${debouncedSearch}"` : 'No groups available'}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="divide-y divide-border">
                                                {paginatedGroups.map((membership) => (
                                                    <div
                                                        key={membership.adUserGroup.id}
                                                        className="px-6 py-3 hover-elevate border-b border-border last:border-b-0"
                                                        data-testid={`row-group-${membership.adUserGroup.id}`}
                                                    >
                                                        <div className="grid gap-4 items-center" style={{ gridTemplateColumns: '1fr 100px 180px' }}>
                                                            <div className="flex items-center gap-3 min-w-0">
                                                                <Shield className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                                                                <div className="min-w-0">
                                                                    <div className="font-medium text-sm truncate">{membership.adUserGroup.Name}</div>
                                                                    <div className="text-xs text-muted-foreground truncate" title={membership.adUserGroup.DistinguishedName}>
                                                                        {membership.adUserGroup.DistinguishedName}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center justify-center gap-1.5">
                                                                <Users className="w-3.5 h-3.5 text-muted-foreground" />
                                                                <span className="text-sm font-medium">{membership.adUserGroup.Members.length}</span>
                                                            </div>
                                                            <div className="flex items-center gap-2 justify-end">
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    className="h-7 px-3 text-xs font-medium text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-950/50 hover:border-blue-300 dark:hover:border-blue-700 transition-colors"
                                                                    onClick={() => {
                                                                        setSelectedGroupMembership(membership);
                                                                        setShowGroupMembersModalOpen(true);
                                                                    }}
                                                                    data-testid={`button-show-users-${membership.adUserGroup.id}`}
                                                                >
                                                                    <Users className="w-3.5 h-3.5 mr-1.5" />
                                                                    Users
                                                                </Button>
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    className="h-7 px-3 text-xs font-medium text-green-600 dark:text-green-400 border-green-200 dark:border-green-800 hover:bg-green-50 dark:hover:bg-green-950/50 hover:border-green-300 dark:hover:border-green-700 transition-colors"
                                                                    onClick={() => {
                                                                        setSelectedGroupMembership(membership);
                                                                        setAddUserToGroupModalOpen(true);
                                                                    }}
                                                                    data-testid={`button-add-user-${membership.adUserGroup.id}`}
                                                                >
                                                                    <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                                                                    Add User
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                    {!isSearching && filteredGroups.length > 0 && renderPaginationControls(
                                        groupsPage,
                                        totalGroupsPages,
                                        filteredGroups.length,
                                        groupsPageSize,
                                        setGroupsPage,
                                        setGroupsPageSize,
                                        'groups'
                                    )}
                                </Card>
                            </TabsContent>
                        </div>
                    </Tabs>
                </div>
            </div>

            {/* Group/OU Users Modal */}
            <Dialog open={groupModalOpen} onOpenChange={setGroupModalOpen}>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Users className="w-5 h-5" />
                            {selectedGroupsList.length > 0 ? (
                                <>Additional Groups ({selectedGroupsList.length})</>
                            ) : (
                                <>Users with {selectedGroup.includes('OU=') ? 'OU' : 'Role'}: {selectedGroup}</>
                            )}
                            <Badge variant="secondary" className="text-xs">
                                Built-in
                            </Badge>
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        {selectedGroupsList.length > 0 ? (
                            /* Show list of groups */
                            <div className="grid grid-cols-2 gap-2">
                                {selectedGroupsList.map((groupName, idx) => (
                                    <Badge
                                        key={idx}
                                        variant="outline"
                                        className="text-sm cursor-pointer hover-elevate p-3 justify-center"
                                        onClick={() => {
                                            setSelectedGroup(groupName);
                                            setSelectedGroupsList([]);
                                        }}
                                        data-testid={`group-list-${groupName}`}
                                    >
                                        {groupName}
                                    </Badge>
                                ))}
                            </div>
                        ) : (
                            <>
                                {/* Search Input */}
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Search users..."
                                        value={groupUserSearch}
                                        onChange={(e) => setGroupUserSearch(e.target.value)}
                                        className="pl-9"
                                        data-testid="input-search-group-users"
                                    />
                                </div>

                                {/* Users Table */}
                                <div className="border rounded-lg overflow-hidden">
                                    <div className="bg-muted/20 px-4 py-3 border-b">
                                        <div className="grid gap-4" style={{ gridTemplateColumns: '2fr 2fr 2fr' }}>
                                            <div className="text-sm font-medium text-muted-foreground">Name</div>
                                            <div className="text-sm font-medium text-muted-foreground">Email</div>
                                            <div className="text-sm font-medium text-muted-foreground">Login Status</div>
                                        </div>
                                    </div>

                                    <div className="max-h-96 overflow-auto">
                                        {(() => {
                                            // Filter users based on selected OU (DistinguishedName contains the OU path)
                                            const groupUsers = adUsers.filter((user: ADUser) =>
                                                user.DistinguishedName.includes(selectedGroup)
                                            ).filter(user =>
                                                groupUserSearch === '' ||
                                                user.Name.toLowerCase().includes(groupUserSearch.toLowerCase()) ||
                                                (user.EmailAddress || '').toLowerCase().includes(groupUserSearch.toLowerCase())
                                            );

                                            return groupUsers.map((user) => (
                                                <div
                                                    key={user.id}
                                                    className="px-4 py-3 border-b last:border-b-0"
                                                    data-testid={`modal-user-${user.id}`}
                                                >
                                                    <div className="grid gap-4 items-center" style={{ gridTemplateColumns: '2fr 2fr 2fr' }}>
                                                        <div className="flex items-center gap-2">
                                                            <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                                            <span className="font-medium">{user.Name}</span>
                                                        </div>
                                                        <div className="text-sm text-muted-foreground">{user.EmailAddress || '-'}</div>
                                                        <div>
                                                            <Badge
                                                                variant={user.loginStatus === 'LOGGED' ? 'default' : 'secondary'}
                                                                className="text-xs"
                                                            >
                                                                {user.loginStatus === 'LOGGED' ? 'Logged In' : 'Not Logged'}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                </div>
                                            ));
                                        })()}

                                        {(() => {
                                            const groupUsers = adUsers.filter((user: ADUser) =>
                                                user.DistinguishedName.includes(selectedGroup)
                                            ).filter(user =>
                                                groupUserSearch === '' ||
                                                user.Name.toLowerCase().includes(groupUserSearch.toLowerCase()) ||
                                                (user.EmailAddress || '').toLowerCase().includes(groupUserSearch.toLowerCase())
                                            );

                                            if (groupUsers.length === 0) {
                                                return (
                                                    <div className="text-center py-8">
                                                        <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                                        <h3 className="text-lg font-medium text-muted-foreground">No Users Found</h3>
                                                        <p className="text-sm text-muted-foreground">
                                                            No users match the current search criteria.
                                                        </p>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })()}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="flex justify-end pt-4 sticky bottom-0 bg-white dark:bg-background z-10 border-t border-border">
                        <Button
                            variant="outline"
                            onClick={() => setGroupModalOpen(false)}
                            data-testid="button-close-modal"
                        >
                            Close
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Add User to Group Modal */}
            <Dialog open={addUserToGroupModalOpen} onOpenChange={setAddUserToGroupModalOpen}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <User className="w-5 h-5" />
                            Add User to Group
                            {selectedGroupMembership && (
                                <Badge variant="outline" className="text-xs ml-2">
                                    {selectedGroupMembership.adUserGroup.Name}
                                </Badge>
                            )}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Search users to add..."
                                value={userSearchForGroup}
                                onChange={(e) => setUserSearchForGroup(e.target.value)}
                                className="pl-9"
                                data-testid="input-search-add-user"
                            />
                        </div>

                        <div className="border rounded-lg overflow-hidden">
                            <div className="max-h-80 overflow-y-auto">
                                {(() => {
                                    const currentMembers = selectedGroupMembership?.adUserGroup.Members || [];

                                    const groupDomain = selectedGroupMembership
                                        ? extractDomainFromDN(selectedGroupMembership.adUserGroup.DistinguishedName, new Set())
                                        : null;

                                    const uniqueUsers = Array.from(
                                        new Map(adUsers.map(user => [user.DistinguishedName.toLowerCase(), user])).values()
                                    );

                                    const availableUsers = uniqueUsers.filter(user => {
                                        if (groupDomain) {
                                            const userDomain = extractDomainFromDN(user.DistinguishedName, new Set());
                                            if (userDomain !== groupDomain) return false;
                                        }

                                        const isAlreadyMember = currentMembers.some(memberDN =>
                                            memberDN.toLowerCase() === user.DistinguishedName.toLowerCase()
                                        );

                                        const matchesSearch = userSearchForGroup === '' ||
                                            user.Name.toLowerCase().includes(userSearchForGroup.toLowerCase()) ||
                                            user.SamAccountName.toLowerCase().includes(userSearchForGroup.toLowerCase()) ||
                                            (user.EmailAddress || '').toLowerCase().includes(userSearchForGroup.toLowerCase());

                                        return !isAlreadyMember && matchesSearch;
                                    });

                                    if (availableUsers.length === 0) {
                                        return (
                                            <div className="text-center py-8">
                                                <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                                <p className="text-sm text-muted-foreground">
                                                    {userSearchForGroup ? 'No matching users available to add' : 'All users are already members of this group'}
                                                </p>
                                            </div>
                                        );
                                    }
                                    return availableUsers.map((user) => (
                                        <div
                                            key={user.id}
                                            className="px-4 py-3 border-b last:border-b-0 hover-elevate flex items-center justify-between"
                                            data-testid={`add-user-option-${user.id}`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                                <div>
                                                    <div className="font-medium">{user.Name}</div>
                                                    <div className="text-xs text-muted-foreground">{user.EmailAddress || user.SamAccountName}</div>
                                                </div>
                                            </div>
                                            <Button
                                                size="sm"
                                                onClick={() => {
                                                    setSelectedUserToAdd(user);
                                                    setConfirmAddUserModalOpen(true);
                                                }}
                                                data-testid={`button-add-${user.id}`}
                                            >
                                                Add
                                            </Button>
                                        </div>
                                    ));
                                })()}
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end pt-4">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setAddUserToGroupModalOpen(false);
                                setUserSearchForGroup("");
                            }}
                            data-testid="button-cancel-add-user"
                        >
                            Cancel
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Confirm Add User to Group Modal */}
            <Dialog open={confirmAddUserModalOpen} onOpenChange={setConfirmAddUserModalOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <UserPlus className="w-5 h-5 text-green-600" />
                            Confirm Add User
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Are you sure you want to add this user to the group?
                        </p>

                        {selectedUserToAdd && (
                            <div className="p-3 bg-muted/30 rounded-lg border">
                                <div className="flex items-center gap-2">
                                    <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                    <span className="font-medium">{selectedUserToAdd.Name}</span>
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                    {selectedUserToAdd.EmailAddress || selectedUserToAdd.SamAccountName}
                                </div>
                            </div>
                        )}

                        {selectedGroupMembership && (
                            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                <div className="text-sm text-muted-foreground">
                                    To group: <span className="font-bold text-foreground">{selectedGroupMembership.adUserGroup.Name}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-2 pt-4">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setConfirmAddUserModalOpen(false);
                                setSelectedUserToAdd(null);
                            }}
                            disabled={isAddingUser}
                            data-testid="button-cancel-confirm-add"
                        >
                            Cancel
                        </Button>
                        <Button
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => {
                                if (selectedGroupMembership && selectedUserToAdd) {
                                    handleAddUserToGroup(selectedGroupMembership.adUserGroup.id, selectedUserToAdd);
                                    setConfirmAddUserModalOpen(false);
                                    setSelectedUserToAdd(null);
                                }
                            }}
                            disabled={isAddingUser}
                            data-testid="button-confirm-add"
                        >
                            {isAddingUser ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Adding...
                                </>
                            ) : (
                                "Confirm"
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Remove User from Group Confirmation Modal */}
            <Dialog open={removeUserModalOpen} onOpenChange={setRemoveUserModalOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                            Remove User from Group
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Are you sure you want to remove this user from the group?
                        </p>

                        {selectedMemberToRemove && (
                            <div className="p-3 bg-muted/30 rounded-lg border">
                                <div className="flex items-center gap-2">
                                    <User className="w-4 h-4 text-muted-foreground" />
                                    <span className="font-medium">{extractCNFromDN(selectedMemberToRemove)}</span>
                                </div>
                                <div className="text-xs text-muted-foreground mt-1 font-mono">
                                    {selectedMemberToRemove}
                                </div>
                            </div>
                        )}

                        {selectedGroupMembership && (
                            <div className="text-xs text-muted-foreground">
                                From group: <span className="font-medium">{selectedGroupMembership.adUserGroup.Name}</span>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-2 pt-4">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setRemoveUserModalOpen(false);
                                setSelectedMemberToRemove("");
                            }}
                            disabled={isRemovingUser}
                            data-testid="button-cancel-remove"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => selectedGroupMembership && handleRemoveUserFromGroup(selectedGroupMembership.adUserGroup.id, selectedMemberToRemove)}
                            disabled={isRemovingUser}
                            data-testid="button-confirm-remove"
                        >
                            {isRemovingUser ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Removing...
                                </>
                            ) : (
                                "Remove User"
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* User Group Memberships Modal */}
            <Dialog
                open={userGroupsModalOpen}
                onOpenChange={(open) => {
                    setUserGroupsModalOpen(open);
                    if (!open) {
                        setSelectedUserForGroups(null);
                        setUserGroupsSearch("");
                    }
                }}
            >
                <DialogContent className="max-w-3xl max-h-[90vh]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            Group Memberships
                            {selectedUserForGroups && (
                                <span className="font-normal text-muted-foreground ml-2">
                                    for {selectedUserForGroups.Name}
                                </span>
                            )}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        {selectedUserForGroups && (
                            <>
                                <div className="p-3 bg-muted/30 rounded-lg border">
                                    <div className="flex items-center gap-3">
                                        <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                        <div>
                                            <div className="font-medium">{selectedUserForGroups.Name}</div>
                                            <div className="text-xs text-muted-foreground">{selectedUserForGroups.EmailAddress || selectedUserForGroups.SamAccountName}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Search group memberships..."
                                        value={userGroupsSearch}
                                        onChange={(e) => setUserGroupsSearch(e.target.value)}
                                        className="pl-9"
                                        data-testid="input-search-user-groups"
                                    />
                                </div>

                                <div className="border rounded-lg overflow-hidden max-h-[60vh] overflow-auto">
                                    {(() => {
                                        const userGroups = getUserGroupsForUser(selectedUserForGroups);

                                        const filteredUserGroups = userGroups.filter(m => {
                                            if (!userGroupsSearch) return true;
                                            const query = userGroupsSearch.toLowerCase();

                                            // Convert all fields to lowercase for case-insensitive search
                                            const name = (m.adUserGroup.Name || "").toLowerCase();
                                            const sam = (m.adUserGroup.SamAccountName || "").toLowerCase();
                                            const dn = (m.adUserGroup.DistinguishedName || "").toLowerCase();
                                            const cn = (m.adUserGroup.cn || "").toLowerCase();
                                            const id = String(m.adUserGroup.id || "").toLowerCase();
                                            const membersJoined = m.adUserGroup.Members.join(" ").toLowerCase();

                                            return (
                                                name.includes(query) ||
                                                sam.includes(query) ||
                                                dn.includes(query) ||
                                                cn.includes(query) ||
                                                id.includes(query) ||
                                                membersJoined.includes(query)
                                            );
                                        });
                                        if (filteredUserGroups.length === 0) {
                                            return (
                                                <div className="text-center py-8">
                                                    <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                                    <h3 className="text-lg font-medium text-muted-foreground">No Group Memberships</h3>
                                                    <p className="text-sm text-muted-foreground">
                                                        This user is not a member of any groups.
                                                    </p>
                                                </div>
                                            );
                                        }

                                        return (
                                            <div className="divide-y divide-border max-h-[50vh] overflow-auto">
                                                {filteredUserGroups.map((membership) => (
                                                    <div
                                                        key={membership.adUserGroup.id}
                                                        className="p-4 hover-elevate"
                                                        data-testid={`user-group-${membership.adUserGroup.id}`}
                                                    >
                                                        <div className="flex items-start justify-between gap-4">
                                                            <div className="flex items-start gap-3">
                                                                <Shield className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-1 flex-shrink-0" />
                                                                <div>
                                                                    <div className="font-medium">{membership.adUserGroup.Name}</div>
                                                                    <div className="text-xs text-muted-foreground font-mono truncate" title={membership.adUserGroup.DistinguishedName}>
                                                                        {membership.adUserGroup.SamAccountName}
                                                                    </div>
                                                                    <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                                                                        <Users className="w-3 h-3" />
                                                                        {membership.adUserGroup.Members.length} members
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                                                                onClick={() => {
                                                                    setSelectedGroupMembership(membership);
                                                                    setSelectedMemberToRemove(selectedUserForGroups!.DistinguishedName);
                                                                    setRemoveUserModalOpen(true);
                                                                }}
                                                                data-testid={`button-remove-from-group-${membership.adUserGroup.id}`}
                                                            >
                                                                <UserMinus className="w-3 h-3 mr-1" />
                                                                Remove
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })()}
                                </div>
                            </>
                        )}
                    </div>

                    <div className="flex justify-end pt-4">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setUserGroupsModalOpen(false);
                                setSelectedUserForGroups(null);
                                setUserGroupsSearch("");
                            }}
                            data-testid="button-close-user-groups"
                        >
                            Close
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Show Group Members Modal */}
            <Dialog open={showGroupMembersModalOpen} onOpenChange={(open) => {
                setShowGroupMembersModalOpen(open);
                if (!open) setGroupMembersSearch("");
            }}>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            Group Members
                            {selectedGroupMembership && (
                                <span className="font-normal text-muted-foreground ml-2">
                                    {selectedGroupMembership.adUserGroup.Name}
                                </span>
                            )}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        {selectedGroupMembership && (
                            <>
                                {/* Search and count header */}
                                <div className="flex items-center justify-between">
                                    <div className="relative flex-1 max-w-sm">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input
                                            placeholder="Search members..."
                                            value={groupMembersSearch}
                                            onChange={(e) => setGroupMembersSearch(e.target.value)}
                                            className="pl-9"
                                            data-testid="input-search-group-members"
                                        />
                                    </div>
                                    <Badge variant="secondary" className="text-sm">
                                        {selectedGroupMembership.adUserGroup.Members.length} members
                                    </Badge>
                                </div>

                                <div className="border rounded-lg overflow-hidden">
                                    <div className="bg-muted/20 px-4 py-3 border-b">
                                        <div className="grid gap-4" style={{ gridTemplateColumns: '2fr 2fr 1fr' }}>
                                            <div className="text-sm font-medium text-muted-foreground">NAME</div>
                                            <div className="text-sm font-medium text-muted-foreground">ACCOUNT</div>
                                            <div className="text-sm font-medium text-muted-foreground">ACTIONS</div>
                                        </div>
                                    </div>
                                    <div className="max-h-96 overflow-auto">
                                        {(() => {
                                            const members = selectedGroupMembership.adUserGroup.Members
                                                .filter(memberDN => {
                                                    if (!groupMembersSearch) return true;
                                                    const cn = extractCNFromDN(memberDN);
                                                    return cn.toLowerCase().includes(groupMembersSearch.toLowerCase()) ||
                                                        memberDN.toLowerCase().includes(groupMembersSearch.toLowerCase());
                                                });

                                            if (members.length === 0) {
                                                return (
                                                    <div className="text-center py-8">
                                                        <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                                        <p className="text-sm text-muted-foreground">
                                                            {groupMembersSearch ? 'No members match your search' : 'No members in this group'}
                                                        </p>
                                                    </div>
                                                );
                                            }

                                            return members.map((memberDN, idx) => {
                                                const memberName = extractCNFromDN(memberDN);
                                                const samMatch = memberDN.match(/CN=([^,]+)/);
                                                const samName = samMatch ? samMatch[1].toLowerCase().replace(/\s+/g, '.') : memberName.toLowerCase();

                                                return (
                                                    <div
                                                        key={idx}
                                                        className="px-4 py-3 border-b last:border-b-0 hover-elevate"
                                                        data-testid={`group-member-${idx}`}
                                                    >
                                                        <div className="grid gap-4 items-center" style={{ gridTemplateColumns: '2fr 2fr 1fr' }}>
                                                            <div className="flex items-center gap-2">
                                                                <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                                                <span className="font-medium truncate">{memberName}</span>
                                                            </div>
                                                            <div className="text-sm text-muted-foreground font-mono truncate" title={memberDN}>
                                                                {samName}
                                                            </div>
                                                            <div>
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                                                                    onClick={() => {
                                                                        setSelectedMemberToRemove(memberDN);
                                                                        setRemoveUserModalOpen(true);
                                                                    }}
                                                                    data-testid={`button-remove-member-${idx}`}
                                                                >
                                                                    <UserMinus className="w-3 h-3 mr-1" />
                                                                    Remove
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            });
                                        })()}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="flex justify-end pt-4">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowGroupMembersModalOpen(false);
                                setGroupMembersSearch("");
                            }}
                            data-testid="button-close-group-members"
                        >
                            Close
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* =====================================================================
          OU Users Modal
          =====================================================================
          Displays users within a selected Organization Unit.
          
          API: GET /api/organization-units/{OU-ID}/users
          Example: GET https://phanydev1.whiteswansec.io/api/organization-units/1/users
          
          Response: Array of ADUser objects with fields:
          - id: Unique identifier
          - Name: Display name (e.g., "SHEENA_NICHOLS")
          - SamAccountName: SAM account name
          - DistinguishedName: Full AD path
          - isServiceAccount: Boolean (typically false)
          - loginStatus: "LOGGED" or "NOT_LOGGED"
          - EmailAddress, UserPrincipalName, MobilePhone (nullable)
          ===================================================================== */}
            <Dialog open={ouUsersModalOpen} onOpenChange={(open) => {
                setOuUsersModalOpen(open);
                if (!open) {
                    setOuUsersSearch("");
                    setSelectedOU(null);
                    setOuUsers([]);
                }
            }}>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Layers className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                            OU Users
                            {selectedOU && (
                                <span className="font-normal text-muted-foreground ml-2">
                                    {selectedOU.name}
                                </span>
                            )}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        {selectedOU && (
                            <>
                                {/* Search and count header */}
                                <div className="flex items-center justify-between">
                                    <div className="relative flex-1 max-w-sm">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input
                                            placeholder="Search users..."
                                            value={ouUsersSearch}
                                            onChange={(e) => setOuUsersSearch(e.target.value)}
                                            className="pl-9"
                                            data-testid="input-search-ou-users"
                                        />
                                    </div>
                                    <Badge variant="secondary" className="text-sm">
                                        {ouUsers.length} users
                                    </Badge>
                                </div>

                                {/* Users table */}
                                <div className="border rounded-lg overflow-hidden">
                                    <div className="bg-muted/20 px-4 py-3 border-b">
                                        <div className="grid gap-4" style={{ gridTemplateColumns: '2fr 2fr 2fr' }}>
                                            <div className="text-sm font-medium text-muted-foreground">NAME</div>
                                            <div className="text-sm font-medium text-muted-foreground">ACCOUNT</div>
                                            <div className="text-sm font-medium text-muted-foreground">UPN</div>
                                        </div>
                                    </div>
                                    <div className="max-h-96 overflow-auto">
                                        {(() => {
                                            // Filter users based on search
                                            const filteredOuUsers = ouUsers.filter(user => {
                                                if (!ouUsersSearch) return true;
                                                const query = ouUsersSearch.toLowerCase();
                                                return user.Name.toLowerCase().includes(query) ||
                                                    user.SamAccountName.toLowerCase().includes(query) ||
                                                    (user.UserPrincipalName || '').toLowerCase().includes(query) ||
                                                    user.DistinguishedName.toLowerCase().includes(query);
                                            });

                                            if (filteredOuUsers.length === 0) {
                                                return (
                                                    <div className="text-center py-8">
                                                        <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                                        <p className="text-sm text-muted-foreground">
                                                            {ouUsersSearch ? 'No users match your search' : 'No users in this OU'}
                                                        </p>
                                                    </div>
                                                );
                                            }

                                            return filteredOuUsers.map((user, idx) => (
                                                <div
                                                    key={user.id}
                                                    className="px-4 py-3 border-b last:border-b-0"
                                                    data-testid={`ou-user-${user.id}`}
                                                >
                                                    <div className="grid gap-4 items-center" style={{ gridTemplateColumns: '2fr 2fr 2fr' }}>
                                                        <div className="flex items-center gap-2">
                                                            <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                                            <span className="font-medium truncate">{user.Name}</span>
                                                        </div>
                                                        <div className="text-sm text-muted-foreground font-mono truncate">
                                                            {user.SamAccountName}
                                                        </div>
                                                        <div
                                                            className="text-sm text-muted-foreground truncate"
                                                            title={user.UserPrincipalName || ''}
                                                        >
                                                            {user.UserPrincipalName || '-'}
                                                        </div>
                                                    </div>
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="flex justify-end pt-4">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setOuUsersModalOpen(false);
                                setOuUsersSearch("");
                                setSelectedOU(null);
                                setOuUsers([]);
                            }}
                            data-testid="button-close-ou-users"
                        >
                            Close
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* =====================================================================
          OU Devices Modal
          =====================================================================
          Displays devices/computers within a selected Organization Unit.
          
          API: GET /api/organization-units/{OU-ID}/computers
          Example: GET https://phanydev1.whiteswansec.io/api/organization-units/1/computers
          
          Response: Array of ADDevice objects with fields:
          - endpointId: Endpoint identifier (nullable)
          - hasAgent: Boolean indicating if agent is installed
          - os: OS type shorthand (e.g., "WINDOWS", nullable)
          - DNSHostName: Full DNS hostname (nullable)
          - DistinguishedName: Full AD path
          - IPv4Address: IP address (nullable)
          - Name: Device name
          - OperatingSystem: Full OS name (nullable)
          ===================================================================== */}
            <Dialog open={ouDevicesModalOpen} onOpenChange={(open) => {
                setOuDevicesModalOpen(open);
                if (!open) {
                    setOuDevicesSearch("");
                    setSelectedOUForDevices(null);
                    setOuDevices([]);
                }
            }}>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Monitor className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            OU Devices
                            {selectedOUForDevices && (
                                <span className="font-normal text-muted-foreground ml-2">
                                    {selectedOUForDevices.name}
                                </span>
                            )}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        {selectedOUForDevices && (
                            <>
                                {/* Search and count header */}
                                <div className="flex items-center justify-between">
                                    <div className="relative flex-1 max-w-sm">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input
                                            placeholder="Search devices..."
                                            value={ouDevicesSearch}
                                            onChange={(e) => setOuDevicesSearch(e.target.value)}
                                            className="pl-9"
                                            data-testid="input-search-ou-devices"
                                        />
                                    </div>
                                    <Badge variant="secondary" className="text-sm">
                                        {ouDevices.length} devices
                                    </Badge>
                                </div>

                                {/* Devices table */}
                                <div className="border rounded-lg overflow-hidden">
                                    <div className="bg-muted/20 px-4 py-3 border-b">
                                        <div className="grid gap-4" style={{ gridTemplateColumns: '2fr 2fr 2fr 1fr' }}>
                                            <div className="text-sm font-medium text-muted-foreground">NAME</div>
                                            <div className="text-sm font-medium text-muted-foreground">OS</div>
                                            <div className="text-sm font-medium text-muted-foreground">IP ADDRESS</div>
                                            <div className="text-sm font-medium text-muted-foreground">AGENT</div>
                                        </div>
                                    </div>
                                    <div className="max-h-96 overflow-auto">
                                        {(() => {
                                            // Filter devices based on search
                                            const filteredOuDevices = ouDevices.filter(device => {
                                                if (!ouDevicesSearch) return true;
                                                const query = ouDevicesSearch.toLowerCase();
                                                return device.Name.toLowerCase().includes(query) ||
                                                    (device.DNSHostName || '').toLowerCase().includes(query) ||
                                                    (device.OperatingSystem || '').toLowerCase().includes(query) ||
                                                    (device.IPv4Address || '').toLowerCase().includes(query) ||
                                                    device.DistinguishedName.toLowerCase().includes(query);
                                            });

                                            if (filteredOuDevices.length === 0) {
                                                return (
                                                    <div className="text-center py-8">
                                                        <Monitor className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                                        <p className="text-sm text-muted-foreground">
                                                            {ouDevicesSearch ? 'No devices match your search' : 'No devices in this OU'}
                                                        </p>
                                                    </div>
                                                );
                                            }

                                            return filteredOuDevices.map((device, idx) => (
                                                <div
                                                    key={`${device.Name}-${idx}`}
                                                    className="px-4 py-3 border-b last:border-b-0"
                                                    data-testid={`ou-device-${device.Name}`}
                                                >
                                                    <div className="grid gap-4 items-center" style={{ gridTemplateColumns: '2fr 2fr 2fr 1fr' }}>
                                                        <div className="flex items-center gap-2">
                                                            <Monitor className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                                            <span className="font-medium truncate">{device.Name}</span>
                                                        </div>
                                                        <div
                                                            className="text-sm text-muted-foreground truncate"
                                                            title={device.OperatingSystem || ''}
                                                        >
                                                            {device.OperatingSystem || device.os || '-'}
                                                        </div>
                                                        <div className="text-sm text-muted-foreground font-mono">
                                                            {device.IPv4Address || '-'}
                                                        </div>
                                                        <div>
                                                            {device.hasAgent ? (
                                                                <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                                                    Active
                                                                </Badge>
                                                            ) : (
                                                                <Badge variant="secondary" className="text-xs">
                                                                    None
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="flex justify-end pt-4">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setOuDevicesModalOpen(false);
                                setOuDevicesSearch("");
                                setSelectedOUForDevices(null);
                                setOuDevices([]);
                            }}
                            data-testid="button-close-ou-devices"
                        >
                            Close
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}